import { gql } from 'graphql-request';
import { fragmentLink, fragmentMedia } from '$lib/api/fragments';
import { getSectionsQuery } from '$lib/util/pagebuilder';
import { getDirectus, getDirectusRaw } from '$lib/util/directus';

export function queryPage(slug: string, locale: string) {
	return gql`
  ${fragmentLink}
  ${fragmentMedia}

  query {
    pages(
      filter: { 
        translations: {
          languages_code: {
            code: {
              _eq: "${locale}"
            }
          }
          slug: {
            _eq: "${slug}"
          }
        }
      }
    ) {
      translations {
        languages_code {
          code
        }
        slug
        seo_title
        seo_description
        seo_image {
          id
          type
        }
      }
      sections {
        id
        collection
        item {
          ${getSectionsQuery('pages')}
        }
      }
    }
  }`;
}

export function querySpecialPage(name: string) {
	return gql`
  ${fragmentLink}
  ${fragmentMedia}

  query {
    special_pages(
      filter: { 
        name: {
          _eq: "${name}"
        }
      }
    ) {
      sections {
        collection
        item {
          ${getSectionsQuery('special-pages')}
        }
      }
    }
  }`;
}

export function queryBlogPage(slug: string, locale: string) {
	return gql`
  ${fragmentLink}
  ${fragmentMedia}

  query {
    posts(
      filter: { 
        translations: {
          languages_code: {
            code: {
              _eq: "${locale}"
            }
          }
          slug: {
            _eq: "${slug}"
          }
        }
      }
    ) {
      id
      preview_image {
        id
        type
        translations {
          languages_code {
            code
          }
          media_description
        }
      }

      header_media {
        id
        type
        translations {
          languages_code {
            code
          }
          media_description
        }
      }
      date
      categories {
        post_category_id {
          icon {
          ...Media
          }
          hover_icon {
          ...Media
          }
          key
          translations {
            languages_code {
              code
            }
            name
          }
        }
      }

      translations {
        languages_code {
          code
        }
        title
        preview_text
      }

      sections {
        collection
        item {
          ${getSectionsQuery('posts')}
        }
      }
    }
  }`;
}

export function queryProductPage(slug: string, locale: string) {
	return gql`

  ${fragmentLink}

  query {
    products(
      filter: {
        product: {
          _contains: "${slug}"
        }
      }
    ) {
      id

      # translations {
      #   languages_code {
      #     code
      #   }
      #   title
      #   preview_text
      # }

      sections {
        collection
        item {
          ${getSectionsQuery('product')}
        }
      }
    }
  }`;
}

export async function getLanguages(fetch, previewToken: string) {
  let languages: any = {};
  try {
    const response = await getDirectus('items/languages?fields=*.*', fetch, undefined, previewToken);

    if (response?.data) {
      languages = response.data;
    }
  } catch (err) {
    console.error(err)
    console.warn('Failed to fetch languages, have you set the backend url in .env? - ', err);
  }
  return languages;
}

export async function getPageList(fetch, previewToken: string) {
  const pageList: Array<any> = [];
  let responsePages: any = [];
  let specialPagesData: any = [];
  try {
    [responsePages, specialPagesData] = await Promise.all([
      getDirectusRaw('items/pages?fields=translations.slug,translations.languages_code', fetch, previewToken),
      getDirectusRaw(
        'items/special_pages?fields=name,related_collection,translations.slug,translations.languages_code',
        fetch,
        previewToken
      )
    ]);

		if (responsePages?.data) {
			for (const page of responsePages.data) {
				const pageObj: any = {};
				for (const lang of page.translations) {
					pageObj[lang.languages_code] = lang.slug;
				}
				pageList.push(pageObj);
			}
		}

    const requests = [];
    for (const specialPageData of specialPagesData.data ?? []) {
      if (!specialPageData.related_collection) continue;

      requests.push(
        getDirectusRaw(
          `items/${specialPageData.related_collection}?fields=translations.slug,translations.languages_code`,
          fetch,
          previewToken
        )
      );
    }

		const responses = await Promise.all(requests);

		for (let i = 0; i < responses.length; i++) {
			const pageType = responses[i].data;

			if (pageType) {
				for (const page of pageType) {
					const pageObj: any = {};
					for (const lang of page.translations) {
						// Get related special page slug prefix

						const slugPrefix: string = specialPagesData.data[i].translations.filter(
							(translation: any) => translation.languages_code === lang.languages_code
						)?.[0]?.slug;

						if (!slugPrefix) continue;

						pageObj[lang.languages_code] = slugPrefix + '/' + lang.slug;
					}
					pageList.push(pageObj);
				}
			}
		}
	} catch (err) {
		console.warn('Failed to fetch page list:', err);
	}
	return {
		specialPagesData: specialPagesData.data,
		pageList
	};
}
